
import { type ReactNode } from "react";
import {
  Modal as AriaModal,
  ModalOverlay as AriaModalOverlay,
  Dialog as AriaDialog,
} from "react-aria-components";
import { X } from "@untitledui/icons";
import { cx } from "@/lib/utils/cx";

interface SheetProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
}

export function Sheet({ isOpen, onClose, title, children, footer, className }: SheetProps) {
  return (
    <AriaModalOverlay
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      className={({ isEntering, isExiting }) =>
        cx(
          "fixed inset-0 z-50 flex items-end",
          "bg-bg-overlay/60 backdrop-blur-sm",
          isEntering && "animate-in fade-in-0",
          isExiting && "animate-out fade-out-0",
        )
      }
    >
      <AriaModal
        className={({ isEntering, isExiting }) =>
          cx(
            "relative w-full bg-primary rounded-t-2xl shadow-2xl border-t border-secondary outline-none max-h-[85vh] flex flex-col",
            isEntering && "animate-in slide-in-from-bottom",
            isExiting && "animate-out slide-out-to-bottom",
            className,
          )
        }
      >
        <AriaDialog className="outline-none flex flex-col max-h-[85vh]">
          {/* Handle */}
          <div className="flex justify-center pt-3 pb-1">
            <div className="w-10 h-1 rounded-full bg-tertiary" />
          </div>

          {/* Header */}
          {title && (
            <div className="flex items-center justify-between px-5 py-3 border-b border-secondary">
              <h2 className="text-base font-semibold text-primary">{title}</h2>
              <button
                onClick={onClose}
                className="text-fg-quaternary hover:text-fg-secondary transition duration-100"
                aria-label="Close sheet"
              >
                <X className="size-5" aria-hidden />
              </button>
            </div>
          )}

          <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>

          {footer && (
            <div className="flex gap-3 px-5 py-4 border-t border-secondary pb-safe">{footer}</div>
          )}
        </AriaDialog>
      </AriaModal>
    </AriaModalOverlay>
  );
}
